<template>
  <div id="event-root" :class="pageClass">
    <mdb-mask :class="maskClass" :overlay="overlay">
      <mdb-container v-if="event.meta">
        <banner-small v-if="loadBanner && event && group" :group="group" :title="event.title"/>

        <mdb-card class="mb-4" v-if="event.unansweredRequiredQuestions">
          <mdb-card-header class="danger-color text-white">
            <h4>
              <mdb-icon icon="octagon-exclamation"/>
              {{ $t('page.event.reservationIncomplete') }}
              <mdb-icon icon="octagon-exclamation"/>
            </h4>
          </mdb-card-header>
          <mdb-card-body class="text-left">
            {{ $t('page.event.reservationIncompleteText') }}
          </mdb-card-body>
        </mdb-card>

        <mdb-card class="mb-4" v-if="translationMode">
          <mdb-card-header>
            <h4>Translations</h4>
          </mdb-card-header>
          <mdb-card-body>
            <div>
              <translate-section category="page" sub-category="event"/>
            </div>
          </mdb-card-body>
        </mdb-card>

        <top-alerts v-if="loggedIn"/>
        <mdb-card-header v-if="!loggedIn" class="mb-2 warning-color text-center top-alert-body">
          <mdb-icon color="danger" icon="star-exclamation"/>
          {{ $t('page.event.wantToParticipate') }}
          <router-link :to="'/quick-connect?event=' + event.eventId">{{
              capitalize($t('common.button.register'))
            }}
          </router-link>
          .
          {{ $t('page.event.ifHaveAccount') }}
          <router-link to="/login">{{ capitalize($t('common.button.login')) }}</router-link>

        </mdb-card-header>

        <mdb-card-header
          v-if="loggedIn && group && group.membershipStatus == 'Inactive' && !group.membershipPending && group.role == 'Public'"
          class="mb-2 warning-color text-center top-alert-body">
          <mdb-icon color="danger" icon="star-exclamation"/>
          {{ $t('page.event.wantToParticipate') }} {{ $t('page.event.needToBeMember', {group: group.name}) }}
          <router-link :to="'/group/' + group.vanityPath + '?doJoin=Y'">{{
              capitalize($t('common.button.join'))
            }}
          </router-link>
          .
        </mdb-card-header>

        <div v-if="event.meta.isAdmin && !showOrgTools" aria-label="Show Edit Tools" class="gp-fixed-action-btn"
             title="Show Edit Tools">
          <a class="btn-floating btn-warning ripple-parent" @click.prevent="showOrgTools = true">
            <mdb-icon fal icon="pencil"/>
          </a>
        </div>
        <div v-if="event.meta.isAdmin && showOrgTools" aria-label="Done Editing" class="gp-fixed-action-btn"
             title="Done Editing">
          <a class="btn-floating btn-success ripple-parent" @click.prevent="showOrgTools = false">
            <mdb-icon icon="check"/>
          </a>
        </div>
        <div v-if="!event.meta.isAdmin && group && group.role=='Event Organizer'" class="gp-fixed-action-btn">
          <a class="btn-floating btn-warning ripple-parent" title="Clone Event" @click.prevent="clone">
            <mdb-icon fal icon="copy"/>
          </a>
        </div>
        <mdb-navbar class="mb-2" dark v-if="event && showMain && event.meta.isAdmin && showOrgTools">
          <mdb-navbar-toggler>
            <mdb-navbar-nav>
              <mdb-dropdown tag="li" class="nav-item">
                <mdb-dropdown-toggle tag="a" navLink slot="toggle" waves-fixed>{{ $t('page.event.actions') }}
                </mdb-dropdown-toggle>
                <mdb-dropdown-menu>
                  <mdb-dropdown-item v-if="event.meta.allowTakeAttendance" @click="takeAttendance">
                    {{ $t('page.event.takeAttendance') }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item v-if="event.status=='Active'" @click="unpublish">{{
                      $t('page.event.unpublish')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item v-if="event.status=='Active'" @click="cancelModal=true">{{
                      $t('page.event.cancel')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item v-if="!event.repeats" @click="deleteModal=true">{{
                      $t('page.event.delete')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item v-if="event.status=='Draft'" @click="publish">Publish</mdb-dropdown-item>
                  <mdb-dropdown-item @click="clone">{{ $t('page.event.clone') }}</mdb-dropdown-item>
                </mdb-dropdown-menu>
              </mdb-dropdown>
              <mdb-dropdown tag="li" class="nav-item">
                <mdb-dropdown-toggle tag="a" navLink slot="toggle" waves-fixed>{{ $t('page.event.settings') }}
                </mdb-dropdown-toggle>
                <mdb-dropdown-menu>
                  <mdb-dropdown-item @click="showAttendeeRuleEditor=true">{{
                      $t('page.event.costRules')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item @click="showVisibilityRules=true">{{
                      $t('page.event.visibilityRules')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item @click="showOtherSettings=true">{{
                      $t('page.event.otherSettings')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item @click="showRepeats=true">{{ $t('page.event.manageRepeats') }}</mdb-dropdown-item>
                  <mdb-dropdown-item @click="showQuestions">{{
                      capitalize($tc('page.event.question', 2))
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item @click="chooseBackgroundImage">{{
                      $t('page.event.backgroundImage')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item @click="choosePromoImage">{{ $t('page.event.promoImage') }}</mdb-dropdown-item>
                </mdb-dropdown-menu>
              </mdb-dropdown>

              <mdb-dropdown tag="li" class="nav-item">
                <mdb-dropdown-toggle tag="a" navLink slot="toggle" waves-fixed>{{ $t('page.event.communication') }}
                </mdb-dropdown-toggle>
                <mdb-dropdown-menu>
                  <mdb-dropdown-item @click="showVirtual">{{ $t('page.event.virtualAssistant') }}</mdb-dropdown-item>
                  <mdb-dropdown-item @click="openNotification">{{
                      $t('page.event.adhocNotification')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item @click="openCrossPost" v-if="event.status=='Active'">{{
                      $t('page.event.crossPost')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item @click="togglePinned">
                    <span v-if="!event.topPin">{{ $t('page.event.pinEvent') }}</span>
                    <span v-if="event.topPin">{{ $t('page.event.unpinEvent') }}</span>
                  </mdb-dropdown-item>
                  <mdb-dropdown-item @click="toggleComments">
                    <span v-if="!event.disableComments">Disable New Comments</span>
                    <span v-if="event.disableComments">Enable Comments</span>
                  </mdb-dropdown-item>
                </mdb-dropdown-menu>
              </mdb-dropdown>

              <mdb-dropdown tag="li" class="nav-item">
                <mdb-dropdown-toggle tag="a" navLink slot="toggle" waves-fixed>{{ $t('page.event.view') }}
                </mdb-dropdown-toggle>
                <mdb-dropdown-menu>
                  <mdb-dropdown-item v-if="event.status != 'Draft' && event.isRepeatChild"
                                     @click="openRepeatParent">{{
                      $t('page.event.viewParent')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item @click="openPaymentHistory">{{
                      $t('page.event.paymentHistory')
                    }}
                  </mdb-dropdown-item>
                  <mdb-dropdown-item @click="auditHistory">{{ $t('page.event.editHistory') }}</mdb-dropdown-item>
                </mdb-dropdown-menu>
              </mdb-dropdown>

            </mdb-navbar-nav>
          </mdb-navbar-toggler>
        </mdb-navbar>

        <mdb-row v-if="event && showMain">
          <mdb-col class="text-left" :md="rightCol ? '7' : '12'">

            <mdb-card v-if="diagnosticMode" class="mb-4">
              <mdb-card-header>
                <h5>
                  <mdb-icon fal icon="wrench"/>
                  Diagnostic
                </h5>
              </mdb-card-header>
              <mdb-card-body>
                <mdb-btn icon="bolt" rounded size="sm" @click="sendDiagnostics">Send Diagnostics Now</mdb-btn>
              </mdb-card-body>
            </mdb-card>

            <mdb-card class="mb-4">
              <mdb-card-header>
                <h5>
                  <mdb-icon fal icon="ticket"/>
                  {{ $t("page.event.summary") }}
                  <div v-if="event.meta.isAdmin && showOrgTools" class="toolbar-buttons">
                    <mdb-btn color="secondary" icon="pencil" rounded size="sm"
                             @click="openSummaryEditor">Edit
                    </mdb-btn>
                  </div>
                </h5>
              </mdb-card-header>
              <mdb-card-body>
                <h4>
                  <mdb-icon icon="thumbtack" size="xl" v-if="event.topPin" color="primary"/>
                  {{ event.title }}

                  <mdb-badge v-if="event.meta.happeningNow" color="warning">
                    <i class="small">{{ $t("page.event.happeningNow") }}</i>
                  </mdb-badge>
                  <mdb-btn v-if="event.status=='Active' && !event.meta.happeningNow && event.meta.counts.memberGoing"
                           floating
                           icon="calendar-plus"
                           size="sm"
                           tag="a" :title="$t('page.event.addToCalendar')" type=""
                           @click="getIcal"/>
                </h4>
                <h6>
                  <b><span v-if="event.status=='Canceled'">Was </span>
                    <span v-html="event.displayDate"/></b>
                  <span v-if="event.status == 'Canceled'">
                    <mdb-badge color="danger">
                      <i>{{ event.status }}</i>
                    </mdb-badge>
                  </span>
                  <span v-if="event.meta.attendance != 'Unknown'">
                    <mdb-badge :color="event.meta.attendance=='Attended' ? 'success' : 'warning'">
                      {{ event.meta.attendance }}
                    </mdb-badge>
                  </span>
                  <span v-if="event.status == 'Draft'">
                    <mdb-badge color="warning">
                      <i>{{ event.status }}</i>
                    </mdb-badge>
                  </span>
                </h6>
                <h6 v-if="showTimezoneMessage" class="text-danger">
                  <i>Warning: The time above isn't in your time zone ({{ browserTimezone }}).
                    <router-link v-if="loggedIn" to="/user/profile">Update Timezone</router-link>
                    .
                  </i>
                </h6>
                <div v-if="event.spansDays && event.rangeDays && event.rangeDays.length==1">
                  <h6><b><i>Every {{ weekdays[event.rangeDays[0]] }}</i></b></h6>
                </div>
                <div v-if="event.spansDays && event.rangeDays && event.rangeDays.length>1">
                  <h6><b><i>Days:</i></b></h6>
                  <ul>
                    <li v-for="day in event.rangeDays" :key="day">
                      {{ weekdays[day] }}
                    </li>
                  </ul>
                </div>
                <div v-for="(price,i) in event.pricing" v-if="event.pricing.length>0" v-bind:key="i">
                  {{ price }}
                </div>

                <div v-if="event.meta && event.meta.memberRole != 'Public' ">
                  <h5 id="myStatus">{{ event.meta.myStatus }}
                    <span v-if="event.meta.counts.memberGoing && event.meta.counts.memberGuests > 0">
                      (+ {{ event.meta.counts.memberGuests }} {{
                        event.meta.counts.memberGuests == 1 ? 'Guest' :
                          'Guests'
                      }})
                    </span>
                  </h5>
                  <div v-if="event.meta.allowJitsiStart && (event.meta.counts.memberGoing || event.meta.isAdmin)">
                    <mdb-btn @click="jitsiJoin" icon="cloud">{{ $t("page.event.joinOnlineEvent") }}</mdb-btn>
                    <div v-if="alternateJoin" v-html="alternateJoin"></div>
                  </div>
                  <span v-if="event.meta.spotsLeft && !event.meta.closeReservations">
                    <i>{{ event.meta.spotsLeft }}</i><br/>
                  </span>

                  <span v-if="event.meta.closeReservations">
                    <span
                      v-if="!event.meta.changeAfterClose && (event.meta.counts.memberGoing || event.meta.counts.memberWaiting)">
                      <i>You may no longer make changes to your {{ $tc("common.label.reservation", 1) }}. Please
                        contact the
                        {{ $tc("common.label.host", 1) }} if you need
                        to make changes.</i>
                    </span>
                    <span v-else>
                      <span v-if="event.meta.openDate">
                        <i>Will open for reservations {{ event.meta.openDate }}</i>
                      </span>
                      <span v-else>
                        <i>Event closed to new reservations.</i>
                      </span>
                    </span>
                    <br/>
                  </span>

                  <rsvp-controls v-show="event && event.meta" :event="event" mode="self"
                                 @rsvpChanged="rsvpChanged"></rsvp-controls>
                  <div v-if="event && event.status == 'Active'" class="text-right text-info small">
                    <mdb-icon icon="eye"></mdb-icon>
                    {{ $t("page.event.seenBy") }} {{ event.viewedBy }}
                  </div>
                </div>
              </mdb-card-body>
              <mdb-card-footer v-if="showOrgTools && event.status != 'Draft' && (event.repeats || event.isRepeatChild)">
                <span class="text-info small">
                  <mdb-icon icon="exclamation-circle"/>
                  This {{ $tc("common.label.event", 1) }} is part of a series.
                  <router-link v-if="event.isRepeatChild" :to="'/event/show/' + event.parentId">Go to parent
                    {{ $tc("common.label.event", 1) }}.
                  </router-link>
                </span>
              </mdb-card-footer>

            </mdb-card>
            <mdb-card class="mb-4">
              <mdb-card-header>
                <h5>
                  <mdb-icon fal icon="info-circle"/>
                  {{ $t("page.event.details") }}
                  <div v-if="event.meta && event.meta.isAdmin && showOrgTools"
                       class="toolbar-buttons">
                    <mdb-btn color="secondary" icon="pencil" rounded size="sm"
                             @click="openDetailsEditor">Edit
                    </mdb-btn>
                  </div>
                </h5>
              </mdb-card-header>
              <mdb-card-body>
                <user-content id="gp-event-description" :content="event.details"/>
              </mdb-card-body>
            </mdb-card>

            <mdb-card class="mb-4">
              <mdb-card-header>
                <h5>
                  <mdb-icon fal icon="map-marker"/>
                  {{ $t("page.event.location") }}
                  <div v-if="event.meta && event.meta.isAdmin && showOrgTools "
                       class="toolbar-buttons">
                    <mdb-btn color="secondary" icon="pencil" rounded size="sm"
                             @click="openLocationEditor">{{ capitalize($t("common.button.edit")) }}
                    </mdb-btn>
                  </div>
                </h5>
              </mdb-card-header>
              <mdb-card-body v-if="!event.useJitsi">
                <h5>{{ event.venue }}</h5>
                <h6 v-if="event.address">{{ event.address }}</h6>
                <user-content id="venue-description" :content="event.venueDescr"/>
                <user-content v-if="event.venueExtraInfo" id="venue-extra-info" :content="event.venueExtraInfo"/>
                <div>
                  <mdb-btn v-if="event.address" :href="'https://maps.google.com/?q=' + event.address"
                           icon="location-arrow" rounded
                           size="sm"
                           tag="a"
                           target="_blank" type="">View on Google Maps
                  </mdb-btn>
                  <mdb-btn v-if="event.venueUrl" :href="event.venueUrl" icon="map-marker-question" rounded size="sm"
                           tag="a"
                           target="_blank"
                           type="">See More
                  </mdb-btn>
                  <span v-if="group && event.meta && event.meta.isAdmin && showOrgTools " class="text-right">
                    <router-link :to="'/group/' + group.vanityPath + '/venue/' +event.venueId">
                      <mdb-btn icon="external-link" rounded size="sm">Update Venue Details</mdb-btn>
                    </router-link>
                    <mdb-btn color="secondary" icon="pencil" rounded size="sm"
                             @click="openExtraInfoEditor">{{ $tc("common.label.event", 1) }}-specific Info
                    </mdb-btn>
                  </span>
                </div>
                <div v-if="venueGallery.length != 0 && event.meta.memberRole !='Public'">
                  <mdb-carousel :items="venueGallery.length" controlls indicators multi slide>
                    <template v-for="(img,i) in venueGallery" #[i+1]>
                      <mdb-card :key="i">
                        <mdb-card-image :alt="'Image of '+img.title" :src="img.thumbUrl"
                                        class="clickable"
                                        @click.native="showLightbox(i)"></mdb-card-image>
                        <mdb-card-body>
                          {{ img.title }}
                        </mdb-card-body>
                      </mdb-card>
                    </template>
                  </mdb-carousel>
                  <cust-lightbox :imgs="lbImgs" :index="lbIndex" :visible="lbVisible"
                                 @hide="handleHide"></cust-lightbox>
                  <div class="text-center"><b><i>Images are from past events at this location.</i></b>
                  </div>
                </div>
              </mdb-card-body>
              <mdb-card-body v-if="event && event.useJitsi">
                <div v-if="event.meta.allowJitsiStart && (event.meta.counts.memberGoing || event.meta.isAdmin)">
                  <mdb-btn @click="jitsiJoin" icon="cloud">{{ $t("page.event.joinOnlineEvent") }}</mdb-btn>
                  <mdb-btn v-if="event.meta.isAdmin" @click="manageGuests=true">{{ $t("page.event.manageGuests") }}
                  </mdb-btn>
                  <div v-if="alternateJoin" v-html="alternateJoin"></div>
                </div>
                <div v-else>
                  {{ $t("page.event.onlineEventMessage") }}
                </div>
              </mdb-card-body>
            </mdb-card>
          </mdb-col>
          <mdb-col class="text-left" md="5" v-if="rightCol">
            <mdb-card v-if="group && event.socialUrl" class="mb-2">
              <mdb-card-header>Share On Social Media</mdb-card-header>
              <mdb-card-body class="text-primary">
                <social-sharing :description="'An event hosted by ' + group.name + '.'"
                                :title="event.title"
                                :url="event.socialUrl"
                                inline-template>
                  <div>
                    <network network="facebook" style="cursor:pointer;">
                      <i class="fab fa-facebook"></i> Facebook
                    </network>
                    <network network="twitter" style="cursor:pointer;">
                      <i class="fab fa-x-twitter"></i> Twitter
                    </network>
                    <network network="linkedin" style="cursor:pointer;">
                      <i class="fab fa-linkedin"></i> LinkedIn
                    </network>
                    <network network="email" style="cursor:pointer;">
                      <i class="fa fa-envelope"></i> Email
                    </network>
                  </div>
                </social-sharing>
              </mdb-card-body>
            </mdb-card>

            <mdb-card v-if="(event.pages && event.pages.length >0) || (event.meta.isAdmin && showOrgTools)"
                      class="mb-4">
              <mdb-card-header class="warning-card-header">
                <h5>
                  <mdb-icon fal icon="exclamation-circle"/>
                  {{ $t("page.event.importantInformation") }}
                  <div v-if="event.meta && event.meta.isAdmin && showOrgTools"
                       class="toolbar-buttons">
                    <mdb-btn color="secondary" icon="pencil" rounded size="sm"
                             @click="showPages = true">Edit
                    </mdb-btn>
                  </div>
                </h5>
              </mdb-card-header>
              <mdb-card-body>
                <div v-for="page in event.pages" v-bind:key="page.id">
                  <router-link v-if="group" :to="'/group/' + group.vanityPath + '/docs/' + page.uri">
                    {{ page.title }}
                  </router-link>
                </div>
                <span v-if="event.meta.isAdmin && event.pages.length==0">
                  <i>This section will not show to {{ $tc("common.label.member", 2) }} unless you add a link to a
                    document.</i>
                </span>
              </mdb-card-body>
            </mdb-card>
            <mdb-card v-if="(event.hosts && event.hosts.length >0) || (event.meta.isAdmin && showOrgTools)"
                      class="mb-4">
              <mdb-card-header>
                <h5>
                  <mdb-icon fal icon="address-card"/>
                  {{ capitalize($tc("common.label.host", 1)) }}
                  <div
                    v-if="event.meta && event.meta.isAdmin  && showOrgTools && group && group.role != 'Event Organizer'"
                    class="toolbar-buttons">
                    <mdb-btn color="secondary" icon="pencil" rounded size="sm"
                             @click="showHost = true">Edit
                    </mdb-btn>
                  </div>
                </h5>
              </mdb-card-header>
              <mdb-card-body>
                <div class="md-12">
                  <span v-for="host in event.hosts" v-bind:key="host.id">
                    <a role="button" @click="showMember(host)">
                      <mdb-chip :alt="host.name" :src="host.avatar" size="lg" waves>{{ host.name }}</mdb-chip>
                    </a>
                  </span>
                  <span v-if="event.meta.isAdmin && event.hosts.length==0">
                    <i>This section will not show to {{ $tc("common.label.member", 2) }} unless you add
                      {{ hostWithArticle }}.</i>
                  </span>
                </div>
              </mdb-card-body>
            </mdb-card>
            <mdb-card v-if="(event.hosts && event.hosts.length == 0 && event.meta.mayHost)"
                      class="mb-4">
              <mdb-card-header>
                <h5>
                  <mdb-icon fal icon="address-card"/>
                  {{ $t("page.event.noHost") }}
                </h5>
              </mdb-card-header>
              <mdb-card-body>
                <div class="md-12">
                  <p> {{ $t("page.event.noHostVolunteer") }}</p>
                  <p class="text-center">
                    <mdb-btn icon="hand-paper"
                             rounded size="sm" @click="makeMeHost">{{ $t("page.event.iVolunteer") }}
                    </mdb-btn>
                  </p>
                </div>
              </mdb-card-body>
            </mdb-card>

            <mdb-card v-if="event.meta && event.meta.showAttendees" class="mb-4">
              <mdb-card-header>
                <h5>
                  <mdb-icon fal icon="users"/>
                  {{ event.meta.goingLabel }} <span
                  v-if="event.meta.counts.going > 0">({{ event.meta.counts.going }})</span>
                  <div class="toolbar-buttons">
                    <mdb-btn v-if="event.meta && event.meta.isAdmin && showOrgTools" color="secondary"
                             icon="pencil" rounded size="sm"
                             @click="openAttendeesEditor">{{ capitalize($t("common.button.edit")) }}
                    </mdb-btn>
                  </div>

                </h5>
              </mdb-card-header>
              <mdb-card-title v-if="event.meta.counts.waiting > 0 && event.status == 'Active'"
                              class="warning-color text-center text-white">
                {{ event.meta.counts.waiting }} <i> {{ capitalize($t("common.label.waiting")) }}</i>
                <mdb-btn v-if="event.meta.waitlistRule != 'Manual' && event.meta.memberRole !='Public'"
                         floating
                         icon="eye"
                         size="sm" tag="a" @click="showWaitlist">{{ capitalize($t("common.button.view")) }}
                </mdb-btn>
              </mdb-card-title>
              <mdb-card-body>
                <div class="md-12">
                  <span v-for="m in attendees"
                        v-if="  (event.status != 'Complete' && m.status == 'Going') || (event.status=='Complete' && m.attendance == 'Attended')">

                    <avatar :avatar="m.avatar" :badge="m.guests > 0? '+'+m.guests : ''" :memberId="m.id"
                            :name="m.name" :reputation="m.reputation"
                            :size="50"/>

                  </span>
                </div>
                <div class="text-center" style="clear:both;">
                  <mdb-btn v-if="event.meta && (!event.meta.isAdmin || !showOrgTools) && attendees.length > 0"
                           icon="eye"
                           rounded size="sm"
                           @click="openAttendeesEditor">
                    {{ capitalize($tc("common.button.seeDetails")) }}
                  </mdb-btn>
                  <mdb-btn v-if="event.meta && event.meta.isAdmin" icon="bullhorn"
                           rounded size="sm"
                           @click="openNotification"> {{ $t("page.event.notify") }}
                  </mdb-btn>
                  <mdb-btn v-if="event.hasQuestions && event.meta.memberRole != 'Public'" icon="question"
                           rounded size="sm" @click="openResponses"> {{ $t("page.event.seeQuestionResponses") }}
                  </mdb-btn>
                  <mdb-btn v-if="event.meta.allowTakeAttendance && event.meta.isAdmin" icon="clipboard-check"
                           rounded
                           size="sm"
                           @click="takeAttendance"> {{ $t("page.event.takeAttendance") }}
                  </mdb-btn>
                  <mdb-btn v-if="!event.meta.allowTakeAttendance && event.meta.isAdmin && event.meta.counts.waiting>0"
                           icon="clipboard-check"
                           rounded
                           size="sm"
                           @click="manageWaitlist">
                    {{ $t("page.event.manageWaitlist") }}
                  </mdb-btn>

                </div>
              </mdb-card-body>
            </mdb-card>

            <mdb-card v-if="event.meta.showImages">
              <mdb-card-header>
                <h5>
                  <mdb-icon fal icon="images"/>
                  {{ $t("page.event.eventImages") }}
                </h5>
              </mdb-card-header>
              <mdb-card-body v-if="eventGallery.length==0">
                {{ $t("page.event.noImagesExist") }}
              </mdb-card-body>
              <mdb-card-body v-if="eventGallery.length>0">
                <mdb-carousel :items="eventGallery.length" controlls indicators multi slide>
                  <template v-for="(img,i) in eventGallery" #[i+1]>
                    <mdb-card :key="i">
                      <mdb-card-image :alt="'Image of '+img.title" :src="img.thumbUrl"
                                      class="clickable"
                                      @click.native="showLightbox2(i)"></mdb-card-image>
                      <mdb-card-body>
                        <div v-if="img.caption" class="text-info small">{{ img.caption }}</div>
                      </mdb-card-body>
                    </mdb-card>
                  </template>
                </mdb-carousel>
                <cust-lightbox v-if="lb2Imgs.length>0" :imgs="lb2Imgs" :index="lb2Index"
                               :visible="lb2Visible"
                               @hide="handleHide2"></cust-lightbox>
              </mdb-card-body>
              <mdb-card-footer v-if="event.meta.memberRole !='Public'" class="text-center">
                <mdb-btn icon="images" rounded size="sm" @click="navToGallery">
                  {{ $t("page.event.gallery") }}
                </mdb-btn>
              </mdb-card-footer>
            </mdb-card>
          </mdb-col>
        </mdb-row>
        <mdb-row v-if="showMain">
          <mdb-col class="mb-4" col="12">
            <messaging v-if="event && event.meta.memberRole != 'Public'"
                       :id="event.eventId"
                       :read-only="event.disableComments"
                       :labels="messagingLabels" type="event"></messaging>
          </mdb-col>
        </mdb-row>
      </mdb-container>

      <member-modal :id="memberModalId" @modalClosed="modalClosed"/>

      <mdb-modal :show="showJitsiModal" size="sm" @close="showJitsiModal = false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>{{ $t("page.event.meetingLink") }}</h5>
        </mdb-modal-header>
        <mdb-modal-body v-if="gettingJitsiLink">
          <mdb-spinner/>
          <h3>{{ $t("page.event.generatingLink") }}</h3>
        </mdb-modal-body>
        <mdb-modal-body v-if="!gettingJitsiLink">
          <div class="text-info">
            {{ $t("page.event.personalLinkText") }}
          </div>
          <a :href="jitsiLink" class="btn btn-default ripple-parent">
            <mdb-icon icon="play"/>
            {{ $t("page.event.joinNow") }}</a>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal :show="showSummaryEditor" size="lg" @close="closeSummaryEditor">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>{{ $t("page.event.editSummary") }}</h5>
        </mdb-modal-header>
        <mdb-modal-body>
          <summary-editor :summary="summaryEditWork" @cancelSummary="closeSummaryEditor"
                          @saveSummary="saveSummary"></summary-editor>
        </mdb-modal-body>
      </mdb-modal>
      <div v-if="showPaymentHistory">
        <payment-history :event="event" @close="closePaymentHistory"/>
      </div>
      <div v-if="showNotification">
        <notification :event="event" @close="closeNotification"/>
      </div>
      <div v-if="showBackground">
        <background-chooser :event="event" @close="closeBackground" @newBackground="newBackground"/>
      </div>
      <div v-if="showPromo">
        <promo-image-chooser :event="event" @close="closePromo" @imageChosen="imageChosen"/>
      </div>

      <div v-if="showDetailsEditor">
        <details-editor :details="detailsEditWork" @cancelDetails="closeDetailsEditor"
                        @saveDetails="saveDetails"/>
      </div>

      <div v-if="showCrossPost">
        <cross-post-editor :event="event" @cancelDetails="closeCrossPost"/>
      </div>

      <div v-if="showQuestionEditor">
        <question-editor :id="event.eventId" @cancel="cancelQuestions" @save="cancelQuestions"/>
      </div>

      <div v-if="showAttendeesEditor">
        <attendees-editor :admin="event.meta.isAdmin" :attendees="attendees" :event="event"
                          :showOrgTools="showOrgTools" :startView="attendeeEditorStart"
                          @cancel="cancelAttendeesEditor"
                          @needReload="loadAttendees" @save="cancelAttendeesEditor"/>
      </div>

      <div v-if="showAttendanceEditor">
        <attendance-editor :attendees="attendees" :event="event" @cancel="cancelAttendanceEditor"
                           @save="cancelAttendanceEditor"/>
      </div>

      <mdb-modal :show="showAttendeeRuleEditor" size="lg" @close="showAttendeeRuleEditor=false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Attendee Rules</h5>
        </mdb-modal-header>
        <mdb-modal-body>
          <attendee-rule-editor :id="event.eventId" @cancel="showAttendeeRuleEditor = false"
                                @save="showAttendeeRuleEditor=false"/>
        </mdb-modal-body>
      </mdb-modal>

      <div v-if="showVirtualEditor">
        <virtual-assistant :id="event.eventId" type="event" @cancel="cancelVirtual" @save="cancelVirtual"/>
      </div>

      <div v-if="showResponses">
        <question-viewer :event="event" @cancel="cancelResponses"/>
      </div>

      <mdb-modal :show="showLocationEditor" size="lg" @close="closeLocationEditor">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Location Details</h5>
        </mdb-modal-header>
        <mdb-modal-body>
          <location-editor :location="locationEditWork"
                           @cancelLocation="closeLocationEditor"
                           @jitsiToggled="jitsiToggled"
                           @saveLocation="saveLocation"/>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal :show="showExtraInfoEditor" size="lg" @close="closeExtraInfoEditor">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Event-Specific Venue Information</h5>
        </mdb-modal-header>
        <mdb-modal-body>
          <extra-info-editor :eventId="event.eventId" :extraInfo="extraInfoEditWork"
                             @cancelExtraInfo="closeExtraInfoEditor"
                             @saveExtraInfo="saveExtraInfo"/>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal :show="showVisibilityRules" size="lg" @close="showVisibilityRules=false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Visibility Rules</h5>
        </mdb-modal-header>
        <mdb-modal-body>
          <visibility-editor :id="event.eventId" @cancel="showVisibilityRules=false"
                             @save="showVisibilityRules=false"/>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal :show="showPages" size="lg" @close="showPages=false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Event Important Information Pages</h5>
        </mdb-modal-header>
        <mdb-modal-body>
          <pages-editor :id="event.eventId" @cancel="showPages=false" @save="pagesUpdated"/>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal :show="showHost" size="lg" @close="hostUpdated">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>{{ capitalize($tc("common.label.host", 1)) }}</h5>
        </mdb-modal-header>
        <mdb-modal-body>
          <host-editor :event="event" :hosts="event.hosts" @cancel="hostUpdated" @save="hostUpdated"/>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal :show="showOtherSettings" size="lg" @close="showOtherSettings=false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Other Settings</h5>
        </mdb-modal-header>
        <mdb-modal-body>
          <other-settings-editor :id="event.eventId" @cancel="showOtherSettings=false"
                                 @save="showOtherSettings=false"/>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal :show="showRepeats" size="lg" @close="showRepeats=false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Event Repeats</h5>
        </mdb-modal-header>
        <mdb-modal-body>
          <repeats-editor :id="event.eventId" @cancel="showRepeats=false" @save="showRepeats=false"/>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal :show="deleteModal" class="text-center" danger size="sm" @close="deleteModal = false">
        <mdb-modal-header :close="false" center>
          <p class="heading">Are you sure you wish to delete this event?</p>
        </mdb-modal-header>
        <mdb-modal-body>
          <p>Deleting this event will remove all traces. No notifications will be sent to members.</p>
          <mdb-icon class="animated rotateIn" icon="times" size="4x"/>
        </mdb-modal-body>
        <mdb-modal-footer center>
          <mdb-btn color="danger" @click="doDelete">Yes</mdb-btn>
          <mdb-btn color="success" @click="deleteModal = false">No</mdb-btn>
        </mdb-modal-footer>
      </mdb-modal>

      <mdb-modal :show="cancelModal" class="text-center" danger size="lg" @close="cancelModal = false">
        <mdb-modal-header :close="false" center>
          <p class="heading">Are you sure you wish to cancel this event?</p>
        </mdb-modal-header>
        <mdb-modal-body>
          <mdb-input v-model="cancelText" placeholder="Optional message to confirmed members"
                     type="textarea"/>
        </mdb-modal-body>
        <mdb-modal-footer center>
          <mdb-btn color="danger" @click="doCancel">Cancel Event</mdb-btn>
          <mdb-btn color="success" @click="cancelModal = false">Never Mind</mdb-btn>
        </mdb-modal-footer>
      </mdb-modal>
      <mdb-modal :show="showAuditModal" class="text-center" size="lg" @close="showAuditModal = false">
        <mdb-modal-header :close="true" center class="secondary-color">
          <h4>Edit History</h4>
        </mdb-modal-header>
        <mdb-modal-body>
          <mdb-tbl responsive striped>
            <mdb-tbl-head class="primary-color text-white">
              <tr>
                <th>Date/Time</th>
                <th>Action</th>
                <th>User</th>
              </tr>
            </mdb-tbl-head>
            <mdb-tbl-body>
              <tr v-for="a in auditData" class="text-left">
                <td>{{ a.stamp }}</td>
                <td>{{ a.action }}</td>
                <td>{{ a.user }}</td>
              </tr>
            </mdb-tbl-body>
          </mdb-tbl>
        </mdb-modal-body>
        <mdb-modal-footer center>
          <mdb-btn color="success" icon="times-circle" @click="showAuditModal = false">Close</mdb-btn>
        </mdb-modal-footer>
      </mdb-modal>

      <mdb-modal :show="showPublishModal" size="lg" @close="showPublishModal = false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Publish Event</h5>
        </mdb-modal-header>
        <mdb-modal-body class="text-left">
          <p>You may choose to publish this event and announce it immediately. If you choose
            to announce it immediately, any announce tasks set up in the virtual assistant will
            then be ignored. If you publish only, this event will be announced based on the virtual
            assistant rules if they exist, or on system default rules if they don't.
          </p>
          <p>
            <mdb-btn icon="bullhorn" @click="doPublishNow(true)">Publish & Announce Now</mdb-btn>
            <mdb-btn icon="scroll" @click="doPublishNow(false)">Publish Only</mdb-btn>

          </p>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal :show="showUnpublishModal" size="lg" @close="showUnpublishModal = false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Parent Event in Series</h5>
        </mdb-modal-header>
        <mdb-modal-body class="text-left">
          <p>This event is the parent of a series. What would you like to do about the child events?</p>
          <p>
            <mdb-btn color="warning" icon="undo" rounded size="sm" @click="setSeriesChoice('unpublish')"/>
            Unpublish the child events
          </p>

          <p>
            <mdb-btn color="danger" icon="trash" rounded size="sm" @click="setSeriesChoice('delete')"/>
            Delete the child events. They will be re-created when you re-publish the event.
          </p>
          <p>
            <mdb-btn color="success" icon="times-circle" rounded size="sm"
                     @click="showUnpublishModal=false"/>
            Never mind - Don't Unpublish
          </p>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal v-if="showWaitlistModal" size="lg" @close="showWaitlistModal = false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Waitlist</h5>
        </mdb-modal-header>
        <mdb-modal-body class="text-left">
          <div class="text-info">Precedence Rule: {{ event.meta.waitlistRule }}</div>
          <div v-for="m in waitlist" :key="m.id">
            <div>
              <avatar :avatar="m.avatar" :badge="m.guests > 0? '+'+m.guests : ''" :memberId="m.id"
                      :name="m.name" :reputation="m.reputation"
                      :size="50"/>
              <h5>{{ m.name }}</h5>
              <div v-if="event.meta.waitlistRule == 'By RSVP Date'"><i>Reserved <span v-html="m.reserved"/></i></div>
              <div v-if="event.meta.waitlistRule == 'By Seniority'"><i>Member since <span v-html="m.joined"/></i></div>
            </div>
            <div style="clear:both;">&nbsp;</div>
          </div>
        </mdb-modal-body>
      </mdb-modal>

      <yes-no-modal v-if="cloneModal"
                    question="Are you sure you want to clone this event?"
                    @no="cloneModal = false"
                    @yes="doClone('event')"/>

      <mdb-modal v-if="manageGuests" size="lg" @close="manageGuests = false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Manage Guests</h5>
        </mdb-modal-header>
        <mdb-modal-body class="text-left">
          <guest-manager :event="event"/>
        </mdb-modal-body>
      </mdb-modal>

      <mdb-modal v-if="cloneTaskModal" size="lg" @close="cloneTaskModal = false">
        <mdb-modal-header :close="true" class="primary-color text-center text-white">
          <h5>Cloning Event</h5>
        </mdb-modal-header>
        <mdb-modal-body class="text-left">
          <h5>What about Virtual Assistant Tasks?</h5>
          <div>
            <mdb-btn size="sm" rounded @click="doClone('event')" icon="check">Select</mdb-btn>
            Copy from this event
          </div>
          <div v-if="cloneTaskOptions.groupTasks > 0">
            <mdb-btn size="sm" rounded @click="doClone('group')" icon="check">Select</mdb-btn>
            Use group defaults
          </div>
          <div v-if="cloneTaskOptions.userTasks > 0">
            <mdb-btn size="sm" rounded @click="doClone('user')" icon="check">Select</mdb-btn>
            Use my personal defaults
          </div>
          <div>
            <mdb-btn color="danger" size="sm" rounded @click="cloneTaskModal = false" icon="times-circle">Cancel
            </mdb-btn>
          </div>

        </mdb-modal-body>
      </mdb-modal>

    </mdb-mask>
  </div>
</template>

<script>
import api from '../../services/groupsPlaceApi.js'
import TopAlerts from "../common/TopAlerts";
import {
  mdbBadge,
  mdbBtn,
  mdbBtnFixed,
  mdbBtnFixedItem,
  mdbCard,
  mdbCardBody,
  mdbCardFooter,
  mdbCardGroup,
  mdbCardHeader,
  mdbCardImage,
  mdbCardText,
  mdbCardTitle,
  mdbCarousel,
  mdbChip,
  mdbCol,
  mdbContainer,
  mdbDropdown,
  mdbDropdownItem,
  mdbDropdownMenu,
  mdbDropdownToggle,
  mdbIcon,
  mdbInput,
  mdbListGroup,
  mdbListGroupItem,
  mdbMask,
  mdbModal,
  mdbModalBody,
  mdbModalFooter,
  mdbModalHeader,
  mdbNavbar,
  mdbNavbarBrand,
  mdbNavbarNav,
  mdbNavbarToggler,
  mdbNavItem,
  mdbPopover,
  mdbRow,
  mdbSelect,
  mdbSpinner,
  mdbTbl,
  mdbTblBody,
  mdbTblHead,
  mdbTooltip,
  mdbView
} from 'mdbvue';
import BannerSmall from "../group/BannerSmall"
import MemberModal from "../group/MemberModal"
import Avatar from "../common/Avatar"
import UserContent from "../common/UserContent"
import RsvpControls from "./RsvpControls";
import SummaryEditor from "./SummaryEditor"
import DetailsEditor from "./DetailsEditor"
import LocationEditor from "./LocationEditor"
import ExtraInfoEditor from "./ExtraInfoEditor";
import VisibilityEditor from "./VisibilityEditor";
import OtherSettingsEditor from "./OtherSettingsEditor";
import QuestionEditor from "./QuestionEditor";
import VirtualAssistant from "./VirtualAssistant";
import AttendeeRuleEditor from "./AttendeeRuleEditor";
import PagesEditor from "./PagesEditor";
import HostEditor from "./HostEditor";
import RepeatsEditor from "./RepeatsEditor";
import AttendeesEditor from "./AttendeesEditor";
import AttendanceEditor from "./AttendanceEditor";
import Messaging from "../common/Messaging";
import QuestionViewer from "./QuestionViewer";
import PaymentHistory from "./PaymentHistory";
import Notification from "./Notification";
import CrossPostEditor from "./CrossPostEditor";
import BackgroundChooser from "../group/BackgroundChooser";
import PromoImageChooser from "../group/PromoImageChooser";
import Vue from "vue";
import download from 'js-file-download';
import YesNoModal from "../common/YesNoModal";
import timezone from "../../services/timezone";
import moment from 'moment'
import CustLightbox from "../common/CustLightbox";
import articles from 'articles'
import GuestManager from "../common/GuestManager";
import translateSection from "../common/TranslateSection";

export default {
  name: 'Event-Viewer',
  components: {
    RsvpControls,
    UserContent,
    BannerSmall,
    mdbContainer,
    mdbChip,
    mdbCardGroup,
    mdbCard,
    mdbCardHeader,
    mdbCardBody,
    mdbCardFooter,
    mdbCardTitle,
    mdbCardText,
    mdbCardImage,
    mdbCarousel,
    mdbIcon,
    mdbInput,
    mdbView,
    mdbMask,
    mdbBtn,
    mdbBtnFixed,
    mdbBtnFixedItem,
    mdbRow,
    mdbListGroup,
    mdbListGroupItem,
    mdbCol,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbModalFooter,
    mdbSelect,
    mdbTooltip,
    mdbBadge,
    mdbTbl,
    mdbTblHead,
    mdbTblBody,
    mdbPopover,
    mdbSpinner,
    mdbNavbar,
    mdbNavbarBrand,
    mdbNavbarToggler,
    mdbNavbarNav,
    mdbNavItem,
    mdbDropdown,
    mdbDropdownItem,
    mdbDropdownMenu,
    mdbDropdownToggle,
    Avatar,
    MemberModal,
    SummaryEditor,
    DetailsEditor,
    LocationEditor,
    VisibilityEditor,
    OtherSettingsEditor,
    QuestionEditor,
    VirtualAssistant,
    AttendeeRuleEditor,
    AttendanceEditor,
    PagesEditor,
    HostEditor,
    RepeatsEditor,
    AttendeesEditor,
    Messaging,
    QuestionViewer,
    PaymentHistory,
    Notification,
    BackgroundChooser,
    PromoImageChooser,
    YesNoModal,
    ExtraInfoEditor,
    CrossPostEditor,
    CustLightbox,
    TopAlerts,
    GuestManager,
    translateSection
  },
  data() {
    return {
      event: {},
      id: null,
      venueGallery: [],
      eventGallery: [],
      showRsvpModal: false,
      loadBanner: false,
      lbVisible: false,
      lbIndex: 0,
      lbImgs: [],
      lb2Visible: false,
      lb2Index: 0,
      lb2Imgs: [],
      memberModalId: null,
      attendees: [],
      showSummaryEditor: false,
      summaryEditWork: null,
      extraInfoEditWork: null,
      showDetailsEditor: false,
      showMain: true,
      detailsEditWork: null,
      cloneTaskOptions: null,
      showLocationEditor: false,
      showExtraInfoEditor: false,
      showAttendeeRuleEditor: false,
      showOrgTools: false,
      locationEditWork: null,
      showVisibilityRules: false,
      showPages: false,
      showOtherSettings: false,
      deleteModal: false,
      cancelModal: false,
      cancelText: null,
      auditData: [],
      showAuditModal: false,
      showPublishModal: false,
      showUnpublishModal: false,
      seriesChoice: null,
      showQuestionEditor: false,
      showVirtualEditor: false,
      showHost: false,
      showRepeats: false,
      showAttendeesEditor: false,
      showAttendanceEditor: false,
      iconDetail: false,
      showResponses: false,
      showPaymentHistory: false,
      showNotification: false,
      pageClass: "",
      maskClass: "",
      overlay: "",
      showBackground: false,
      showPromo: false,
      enteredFrom: "",
      attendeeEditorStart: "",
      cloneModal: false,
      cloneTaskModal: false,
      weekdays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      browserTimezone: null,
      showTimezoneMessage: false,
      waitlist: null,
      showWaitlistModal: false,
      messagingLabels: {
        type: this.$tc("common.messaging.comment", 1),
        pluralType: this.$tc("common.messaging.comment", 2),
        buttonLabel: this.$t("common.messaging.send")
      },
      showCrossPost: false,
      manageGuests: false,
      alternateJoin: "",
      showJitsiModal: false,
      gettingJitsiLink: false,
      jitsiLink: ""

    }
  },
  computed: {
    group() {
      return this.$store.state.currentGroup
    },
    loggedIn() {
      return this.$store.state.loggedIn
    },
    diagnosticMode() {
      return this.$store.state.diagnosticMode
    },
    userDetails() {
      return this.$store.state.userDetails
    },
    settings() {
      return this.$store.state.settings
    },
    rightCol() {
      if (!this.event) return false
      if ((this.event.pages && this.event.pages.length > 0) || (this.event.meta.isAdmin && this.showOrgTools)) return true
      if ((this.event.hosts && this.event.hosts.length > 0) || (this.event.meta.isAdmin && this.showOrgTools)) return true
      if (this.event.hosts && this.event.hosts.length == 0 && this.event.meta.mayHost) return true
      if (this.event.meta.showImages) return true
      return false
    },
    hostWithArticle() {
      return articles.articlize(this.$tc("common.label.host", 1))
    },
    translationMode() {
      return this.$store.state.translationMode
    }
  },
  mounted() {
    this.browserTimezone = timezone.detectTimeZone()
    this.id = this.$route.params.id;
    this.loadEvent(false);
  },
  metaInfo() {
    return {
      link: [
        {
          rel: "canonical",
          href: process.env.BACKEND_URL + "/event/show/" + this.$route.params.id
        }
      ]
    }
  },
  beforeRouteEnter(to, from, next) {
    if (from) {
      next(vm => {
        vm.enteredFrom = from.name  //save where we came from
      })
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.showAttendeesEditor && to.name == "group-members") {
      next()
      return
    }

    if (this.showMain && !(this.showVisibilityRules || this.showOtherSettings || this.deleteModal || this.cancelModal || this.showAuditModal || this.showUnpublishModal)) {
      next()
      return
    }
    if (to.name != this.enteredFrom) {
      next()
      return
    }

    //We are here so it must be a back button hit while on a sub-page.
    next(false)
    this.cancelAttendeesEditor()
    this.cancelAttendanceEditor()
    this.closeBackground()
    this.closePromo()
    this.cancelResponses()
    this.cancelQuestions()
    this.cancelVirtual()
    this.closePaymentHistory()
    this.closeNotification()
    this.closeDetailsEditor()
    this.closeSummaryEditor()
    this.showVisibilityRules = false
    this.showOtherSettings = false
    this.deleteModal = false
    this.cancelModal = false
    this.showAuditModal = false
    this.showUnpublishModal = false
  },
  methods: {
    openRepeatParent() {
      if (!this.event.isRepeatChild) return
      this.$router.push("/event/show/" + this.event.parentId)
    },
    newBackground(b) {
      this.closeBackground()
      this.setBackground(b)
      window.scrollTo(0, 0)
    },
    setBackground(b) {
      this.pageClass = "full-view";
      this.maskClass = "overflow-auto gp-full-overlay";
      this.overlay = "stylish-strong";
      Vue.nextTick().then(() => {
        let el = document.getElementById("event-root");
        if (el) {
          el.setAttribute("style", "background-image: url('" + b + "');")
        }
      })
    },
    openCrossPost: function () {
      this.showMain = false;
      this.showCrossPost = true
      this.$nextTick().then(() => {
        window.scrollTo(0, this.listScrollPos)
      })
    },
    closeCrossPost: function () {
      this.showCrossPost = false;
      this.showMain = true;
    },
    getIcal: function () {
      this.$store.dispatch("setExecuting", true)
      api.get('/event/ical/' + this.event.eventId).then(
        (results) => {
          download(results.data, "event_" + this.event.eventId + "_calendar.ics")
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    navToGallery: function () {
      this.$router.push("/event/gallery/" + this.event.eventId)
    },
    pagesUpdated: function (e) {
      this.showPages = false;
      this.event.pages = [];
      for (let i = 0; i < e.pages.length; i++) {
        if (e.pages[i].selected) this.event.pages.push(e.pages[i])
      }

    },
    cancelAttendeesEditor: function () {
      if (!this.showAttendeesEditor) return
      this.showAttendeesEditor = false;
      this.showMain = true;
      this.attendeeEditorStart = "";
      this.reloadCounts();
      this.loadAttendees();

    },
    cancelAttendanceEditor: function () {
      this.showAttendanceEditor = false;
      this.showMain = true
    },
    hostUpdated: function () {
      this.showHost = false;
      this.loadAttendees()
    },
    chooseBackgroundImage: function () {
      this.showMain = false;
      this.showBackground = true
    },
    choosePromoImage: function () {
      this.showMain = false;
      this.showPromo = true
    },
    closeBackground: function () {
      this.showMain = true;
      this.showBackground = false
    },
    closePromo: function () {
      this.showMain = true;
      this.showPromo = false
    },
    openAttendeesEditor: function () {
      this.showMain = false;
      this.showAttendeesEditor = true
      this.$nextTick().then(() => {
        window.scrollTo(0, this.listScrollPos)
      })
    },
    openResponses: function () {
      this.showMain = false;
      this.showResponses = true
    },
    cancelResponses: function () {
      this.showMain = true;
      this.showResponses = false
    },
    showQuestions: function () {
      this.showMain = false;
      this.showQuestionEditor = true
    },
    showAttendeeRules: function () {
      this.showAttendeeRuleEditor = true
    },
    cancelQuestions: function () {
      this.showMain = true;
      this.showQuestionEditor = false
    },
    showVirtual: function () {
      this.showMain = false;
      this.showVirtualEditor = true
    },
    cancelVirtual: function () {
      this.showMain = true;
      this.showVirtualEditor = false
      this.$router.replace("/event/show/" + this.event.eventId)
    },
    openExtraInfoEditor: function () {
      this.extraInfoEditWork = this.event.venueExtraInfo
      this.showExtraInfoEditor = true
    },
    closeExtraInfoEditor: function () {
      this.extraInfoEditWork = null
      this.showExtraInfoEditor = false
    },
    saveExtraInfo: function (newInfo) {
      this.event.venueExtraInfo = newInfo
      this.extraInfoEditWork = null
      this.showExtraInfoEditor = false
    },
    openLocationEditor: function () {
      this.locationEditWork = {
        venueId: this.event.venueId,
        venueUrl: this.event.venueUrl,
        address: this.event.address,
        venueDescr: this.event.venueDescr,
        venue: this.event.venue,
        eventId: this.event.eventId,
        status: this.event.status,
        useJitsi: this.event.useJitsi,
        repeats: this.event.repeats || this.event.isRepeatChild
      };
      this.showLocationEditor = true
    },
    closeLocationEditor: function () {
      this.locationEditWork = null;
      this.showLocationEditor = false
    },
    jitsiToggled: function (useJitsi) {
      this.event.useJitsi = useJitsi
      this.showLocationEditor = false
    },

    saveLocation: function (newLocation) {
      Object.assign(this.event, newLocation);
      this.loadVenueGallery();
      this.showLocationEditor = false
    },
    openDetailsEditor: function () {
      this.detailsEditWork = {
        text: this.event.details,
        eventId: this.event.eventId,
        status: this.event.status,
        repeats: this.event.repeats || this.event.isRepeatChild
      };
      this.showDetailsEditor = true;
      this.showMain = false
    },
    openPaymentHistory: function () {
      this.showMain = false;
      this.showPaymentHistory = true
    },
    closePaymentHistory: function () {
      this.showMain = true;
      this.showPaymentHistory = false
    },
    openNotification: function () {
      this.showMain = false;
      this.showNotification = true
    },
    closeNotification: function () {
      this.showMain = true;
      this.showNotification = false
    },
    closeDetailsEditor: function () {
      this.detailsEditWork = null;
      this.showDetailsEditor = false;
      this.showMain = true
    },
    saveDetails: function () {
      this.event.details = this.detailsEditWork.text;
      this.showDetailsEditor = false;
      this.showMain = true
    },
    openSummaryEditor: function () {
      this.summaryEditWork = {
        title: this.event.title,
        id: this.event.eventId,
        startDate: new Date(this.event.startDateRaw),
        endDate: new Date(this.event.endDateRaw),
        updated: false,
        hideEndTime: this.event.hideEndTime,
        publicTitle: this.event.publicTitle,
        status: this.event.status,
        repeats: this.event.repeats || this.event.isRepeatChild,
        rangeDays: this.event.rangeDays
      };
      this.showSummaryEditor = true
    },
    closeSummaryEditor: function () {
      this.summaryEditWork = null;
      this.showSummaryEditor = false
    },
    saveSummary: function (newSummary) {
      Object.assign(this.event, newSummary);
      this.summaryEditWork = null;
      this.showSummaryEditor = false
    },
    imageChosen: function () {
      this.showPromo = false;
      this.showMain = true;
    },
    loadEvent: function (isReload) {
      this.$store.dispatch("setExecuting", true);
      api.get("/event/show/" + this.id).then(
        (results) => {
          this.event = results.data;
          window.scrollTo(0, 0)
          if (this.event.compareStartDate) {
            let browserStart = moment(this.event.startDateRaw).format("YYYYMMDDHHmm")
            if (browserStart != this.event.compareStartDate) this.showTimezoneMessage = true
          }

          if (this.event.background) {
            this.setBackground(this.event.background)
          }
          if (!isReload) {
            if (this.event.status == "Draft" && this.event.meta.isAdmin) this.showOrgTools = true
            if (!this.group || this.group.id != this.event.groupId) {
              api.groupBasics(this.event.groupId, "public");
              this.loadBanner = true

            } else {
              this.loadBanner = true
            }
            document.title = this.event.title;

            if (this.event.loadGallery) {
              this.loadVenueGallery()
            }
            if (this.event.meta.showImages) {
              this.loadEventGallery()
            }
          }
          if (this.event.meta.showAttendees) {
            this.loadAttendees()
          }
          if (this.$route.query.ical == 'Y') {
            this.getIcal()
            this.$router.replace("/event/show/" + this.id)
          }

          if (this.$route.params.action) {
            if (this.$route.params.action == "tasks") {
              this.showVirtual()
              this.showOrgTools = true
            }
          }
        },
        (error) => {
          if (error.response.status == 403) {
            this.$router.push({path: '/403', query: {redirect: this.$route.fullPath}})
            return
          }
          if (error.response.status == 306) {
            window.location = error.response.data.url
            return
          }
          this.$notify.error({message: "Could not load event. " + error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    rsvpChanged: function () {
      this.loadEvent(true)
    },
    modalClosed: function () {
      this.memberModalId = null
    },
    loadAttendees: function () {
      api.get("/event/attendees/" + this.event.eventId).then(
        (results) => {
          this.attendees = results.data;
          if (this.event.meta.isAdmin && this.group && this.group.role != 'Admin' && this.group.role != 'Group Organizer') {
            console.log("Looking at attendees to lock!")
            for (let i = 0; i < this.attendees.length; i++) {
              let a = this.attendees[i];
              if (a.memberId == this.group.memberId) a.locked = true
            }
          }

          if (this.$route.query.view == "people" && this.event.meta.isAdmin) {
            this.openAttendeesEditor()
          }
        },
        (error) => {

        })
    },
    reloadCounts: function () {
      if (!this.event.meta.isAdmin) return
      api.get("/event/counts/" + this.event.eventId).then(
        (results) => {
          this.event.meta.counts = results.data
        },
        (error) => {

        })
    },
    clone: function () {
      api.get("/event/cloneTaskOptions/" + this.event.eventId).then(
        (results) => {
          this.cloneTaskOptions = results.data
          if (results.data.groupTasks == 0 && results.data.userTasks == 0) this.cloneModal = true
          else this.cloneTaskModal = true
        },
        (error) => {
          this.$notify.error({message: "Could not get clone options. " + error.response.data, timeOut: 20000})
        })


    },
    doClone: function (taskOption) {
      this.cloneModal = false
      api.post("/event/clone/" + this.event.eventId, {taskOption: taskOption}).then(
        (results) => {
          this.id = results.data.id;
          this.loadEvent(true);
          this.$router.replace("/event/show/" + results.data.id)
          this.$notify.success({
                                 message: "Event cloned successfully. You are now viewing the copy. ",
                                 timeOut: 20000
                               })
        },
        (error) => {
          this.$notify.error({message: "Could not clone event. " + error.response.data, timeOut: 20000})
        })
    },
    makeMeHost: function () {
      api.post("/event/makeMeHost/" + this.event.eventId, {}).then(
        (results) => {
          this.loadEvent(true);
          this.$notify.success({
                                 message: "You are now the host! Thanks for stepping up!",
                                 timeOut: 20000
                               })
        },
        (error) => {
          this.$notify.error({message: "Could not clone event. " + error.response.data, timeOut: 20000})
        })
    },
    togglePinned: function () {
      api.post("/event/togglePinned/" + this.event.eventId, {}).then(
        (results) => {
          this.event.topPin = results.data.topPin
        },
        (error) => {
          this.$notify.error({message: "Could not pin event. " + error.response.data, timeOut: 20000})
        })
    },
    toggleComments: function () {
      api.post("/event/toggleComments/" + this.event.eventId, {}).then(
        (results) => {
          this.event.disableComments = results.data.disableComments
        },
        (error) => {
          this.$notify.error({message: "Could not change commenting. " + error.response.data, timeOut: 20000})
        })
    },
    doCancel: function () {
      api.post("/event/cancel/" + this.event.eventId, {cancelText: this.cancelText}).then(
        (results) => {
          this.cancelModal = false;
          this.$notify.success({message: "Event canceled. ", timeOut: 5000});
          this.event.status = "Canceled"
        },
        (error) => {
          this.$notify.error({message: "Could not cancel event. " + error.response.data, timeOut: 20000})
        })
    },
    doDelete: function () {
      this.deleteModal = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/event/delete/" + this.event.eventId,).then(
        (results) => {
          this.$store.dispatch("setExecuting", false)
          this.$router.push("/group/" + this.group.vanityPath);
          this.$notify.success({message: "Event deleted successfully.", timeOut: 5000})
        },
        (error) => {
          this.$store.dispatch("setExecuting", false)
          this.$notify.error({message: "Could not delete event. " + error.response.data, timeOut: 20000})
        })
    },
    auditHistory: function () {
      api.get("/event/audits/" + this.event.eventId,).then(
        (results) => {
          this.auditData = results.data;
          this.showAuditModal = true
        },
        (error) => {
          this.$notify.error({message: "Could not fetch audit data. " + error.response.data, timeOut: 20000})
        })
    },
    loadVenueGallery: function () {
      if (this.event.meta.memberRole == 'Public') return
      this.venueGallery = [];
      this.lbImgs = [];
      api.get("/venue/gallery/" + this.event.venueId).then(
        (results) => {
          this.venueGallery = results.data;
          results.data.forEach(img => {
                                 this.lbImgs.push({img: img.imageUrl, caption: img.caption})
                               }
          )
        },
        (error) => {

        })
    },
    loadEventGallery: function () {
      api.get("/event/gallery/" + this.event.eventId).then(
        (results) => {
          this.eventGallery = results.data.images;
          results.data.images.forEach(img => {
                                        let caption = "";
                                        if (img.caption) caption = img.caption + " by " + img.by;
                                        else caption = "By " + img.by;
                                        this.lb2Imgs.push({img: img.imageUrl, caption: caption})
                                      }
          )
        },
        (error) => {

        })
    },
    publish: function () {
      if (!this.event.repeats) {
        this.showPublishModal = true
      } else this.doPublishNow(false)
    },
    doPublishNow: function (announceNow) {
      this.showPublishModal = false
      this.$store.dispatch("setExecuting", true);
      api.post("/event/publish/" + this.event.eventId, {announceNow: announceNow}).then(
        (results) => {
          this.event.status = "Active"
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 10000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    setSeriesChoice: function (choice) {
      this.seriesChoice = choice;
      this.showUnpublishModal = false;
      this.unpublish()
    },
    unpublish: function () {
      if (this.event.repeats && !this.seriesChoice) {
        this.showUnpublishModal = true;
        return
      }
      let d = {};
      if (this.event.repeats && this.seriesChoice) d.series = this.seriesChoice;
      this.$store.dispatch("setExecuting", true);
      api.post("/event/unpublish/" + this.event.eventId, d).then(
        (results) => {
          this.event.status = "Draft"
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 10000})
        }).then(() => {
        this.seriesChoice = null;
        this.$store.dispatch("setExecuting", false)
      })
    },
    showMember: function (host) {
      if (this.group.role == "Public") return;
      this.memberModalId = host.id
    },
    showLightbox(index) {
      this.lbIndex = index;
      this.lbVisible = true
    },
    handleHide() {
      this.lbVisible = false
    },
    showLightbox2(index) {
      this.lb2Index = index;
      this.lb2Visible = true
    },
    handleHide2() {
      this.lb2Visible = false
    },
    takeAttendance() {
      this.showOrgTools = true;
      this.showMain = false;
      this.showAttendanceEditor = true
    },
    manageWaitlist() {
      this.showOrgTools = true;
      this.showMain = false;
      this.attendeeEditorStart = "waitlist"
      this.showAttendeesEditor = true;
    },
    sendDiagnostics() {
      this.$store.dispatch("setExecuting", true);
      let payload = {
        event: this.event,
        path: this.$route.fullPath,
        group: this.group
      }
      api.post("/user/diagnostics", payload).then(
        (results) => {
          this.$notify.success({message: "Diagnostic Report Sent. Thank you!", timeOut: 10000})
        },
        (error) => {
          console.error(error.response);
          this.$notify.error({message: error.response, timeOut: 10000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    showWaitlist() {
      this.$store.dispatch("setExecuting", true);
      api.get("/event/waitlist/" + this.event.eventId).then(
        (results) => {
          this.waitlist = results.data
          this.showWaitlistModal = true
        },
        (error) => {
          this.$notify.error({message: error.response, timeOut: 10000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    jitsiJoin() {
      this.showJitsiModal = true
      this.gettingJitsiLink = true
      api.get("/jitsi/eventJwt/" + this.id).then(
        (results) => {
          let url = results.data.url
          url += "?jwt=" + results.data.jwtToken
          this.jitsiLink = url
          this.gettingJitsiLink = false
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
          this.joinRequested = false
          this.showJitsiModal = false
          this.gettingJitsiLink = false
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    }
  }
};
</script>
<style scoped>
.clickable {
  cursor: pointer;
}

.toolbar-buttons {
  float: right;
}

@media screen and (max-width: 499px ) {
  .gp-fixed-action-btn {
    position: fixed;
    right: 5px;
    bottom: 10px;
    z-index: 998;
    margin-bottom: 0;
    overflow: hidden;
    height: 100px;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: end;
    -ms-flex-align: end;
    align-items: flex-end;
  }
}

@media screen and (min-width: 500px ) {

  .gp-fixed-action-btn {
    position: fixed;
    right: 35px;
    top: 100px;
    z-index: 998;
    margin-bottom: 0;
    overflow: hidden;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: end;
    -ms-flex-align: end;
    align-items: flex-end;
    padding: 15px 15px 15px 15px;
    padding-bottom: 15px;
    padding-left: 15px;
    padding-right: 15px;
  }
}

</style>

